/**
 * Mortgage (Bulgaria) — pure calculation logic.
 *
 * Monthly annuity payment:
 *   i = r / 12, n = years × 12
 *   PMT = L × i × (1 + i)^n / ((1 + i)^n − 1)
 *
 * APR (ГПР) is defined by чл. 19 ЗПК / Directive 2014/17/EU as the rate that
 * equates the discounted cash flows of the loan. We only compute it when the
 * user has supplied ALL mandatory one-off and recurring costs — an APR built
 * on guessed fees is misleading, so the UI must hide it otherwise.
 *
 * Regulatory reference limits (БНБ макропруденциални мерки, в сила от
 * 01.10.2024 г. за жилищни кредити, обезпечени с жилищен имот):
 *   LTV ≤ 85%, DSTI ≤ 50%, максимален срок 30 години.
 * These are supervisory caps, not an approval promise — each bank applies its
 * own credit policy on top.
 *
 * Fees:
 *   Такса за вписване на ипотека: 0.1% от обезпечената сума
 *   (Тарифа за държавните такси, събирани от Агенцията по вписванията, чл. 2).
 *   Нотариалната такса е отделна и прогресивна по Тарифа за нотариалните такси.
 */

export const MORTGAGE_VERIFIED_AT = "2026-08-15";

export const REGULATORY_LIMITS = {
  maxLtvPct: 85,
  maxDstiPct: 50,
  maxTermYears: 30,
} as const;

/** Държавна такса за вписване на ипотека — 0.1% от обезпечената сума. */
export const REGISTRATION_FEE_RATE = 0.001;

export interface MortgageInput {
  homePrice: number;
  downPaymentPct: number;
  /** Nominal annual interest rate in percent. */
  interestRatePct: number;
  years: number;
  monthlyNetIncome: number;
  /** Existing monthly debt payments, used for DSTI. */
  otherMonthlyDebt?: number;
}

export interface MortgageResult {
  loanAmount: number;
  downPayment: number;
  ltvPct: number;
  monthlyPayment: number;
  totalPaid: number;
  totalInterest: number;
  registrationFee: number;
  dstiPct: number;
  withinLtvLimit: boolean;
  withinDstiLimit: boolean;
  withinTermLimit: boolean;
}

export function monthlyAnnuityPayment(
  loanAmount: number,
  annualRatePct: number,
  years: number,
): number {
  const i = annualRatePct / 100 / 12;
  const n = Math.round(years * 12);
  if (n <= 0) return 0;
  if (i === 0) return loanAmount / n;
  return (loanAmount * i * Math.pow(1 + i, n)) / (Math.pow(1 + i, n) - 1);
}

export function calculateMortgage({
  homePrice,
  downPaymentPct,
  interestRatePct,
  years,
  monthlyNetIncome,
  otherMonthlyDebt = 0,
}: MortgageInput): MortgageResult {
  const downPayment = (homePrice * downPaymentPct) / 100;
  const loanAmount = Math.max(0, homePrice - downPayment);
  const monthlyPayment = monthlyAnnuityPayment(loanAmount, interestRatePct, years);
  const totalPaid = monthlyPayment * Math.round(years * 12);
  const ltvPct = homePrice > 0 ? (loanAmount / homePrice) * 100 : 0;
  const dstiPct =
    monthlyNetIncome > 0 ? ((monthlyPayment + otherMonthlyDebt) / monthlyNetIncome) * 100 : 0;

  return {
    loanAmount,
    downPayment,
    ltvPct,
    monthlyPayment,
    totalPaid,
    totalInterest: totalPaid - loanAmount,
    registrationFee: loanAmount * REGISTRATION_FEE_RATE,
    dstiPct,
    withinLtvLimit: ltvPct <= REGULATORY_LIMITS.maxLtvPct + 1e-9,
    withinDstiLimit: dstiPct <= REGULATORY_LIMITS.maxDstiPct + 1e-9,
    withinTermLimit: years <= REGULATORY_LIMITS.maxTermYears,
  };
}

export interface AprCosts {
  /** One-off fees paid at drawdown (processing, valuation, registration, notary...). */
  upfrontFees: number;
  /** Recurring monthly costs (servicing fee, property + life insurance per month). */
  monthlyFees: number;
}

/** True only when every mandatory cost field has been explicitly provided. */
export function hasCompleteAprInputs(costs: Partial<AprCosts> | null | undefined): costs is AprCosts {
  return (
    !!costs &&
    typeof costs.upfrontFees === "number" &&
    Number.isFinite(costs.upfrontFees) &&
    costs.upfrontFees >= 0 &&
    typeof costs.monthlyFees === "number" &&
    Number.isFinite(costs.monthlyFees) &&
    costs.monthlyFees >= 0
  );
}

/**
 * Effective annual percentage rate of charge (ГПР), solved numerically.
 * Net amount received = loan − upfront fees; monthly outflow = PMT + monthly fees.
 * Returns the annualised rate in percent: (1 + i)^12 − 1.
 */
export function calculateApr(
  loanAmount: number,
  monthlyPayment: number,
  years: number,
  costs: AprCosts,
): number {
  const n = Math.round(years * 12);
  const net = loanAmount - costs.upfrontFees;
  const outflow = monthlyPayment + costs.monthlyFees;
  if (n <= 0 || net <= 0 || outflow <= 0) return 0;

  const pv = (i: number) => {
    if (i === 0) return outflow * n - net;
    return (outflow * (1 - Math.pow(1 + i, -n))) / i - net;
  };

  let lo = 0;
  let hi = 1; // 100% monthly is far above any realistic solution
  for (let k = 0; k < 200; k++) {
    const mid = (lo + hi) / 2;
    if (pv(mid) > 0) lo = mid;
    else hi = mid;
  }
  const monthly = (lo + hi) / 2;
  return (Math.pow(1 + monthly, 12) - 1) * 100;
}
